const MIN_PREFIX_LENGTH = 4;

/** Lowercase words, so "Coca-Cola Japan" → ["coca", "cola", "japan"]. */
const words = (name: string) =>
  name
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

const startsWithWords = (whole: string[], prefix: string[]) =>
  prefix.every((w, i) => whole[i] === w);

const only = (names: string[]) => (names.length === 1 ? names[0] : null);

/**
 * The client a workspace most likely belongs to, or `null` when there is no
 * single confident match. Names are compared as whole words, ignoring case and
 * punctuation, tried in order:
 * 1. the same words ("PepsiCo" → PepsiCo, "Coca Cola" → Coca-Cola);
 * 2. the client with the most words the workspace starts with ("Danone Indonesia" → Danone);
 * 3. the one client whose name starts with the workspace's words ("Nielsen" → Nielsen IQ).
 * A word is never split ("Kerrygold" doesn't match "Kerry"), very short names
 * never match by prefix, and ties give no match.
 */
export const matchClientToWorkspace = (
  workspace: string,
  clients: string[],
): string | null => {
  const w = words(workspace);
  if (w.length === 0) return null;
  const keyed = clients
    .map((name) => ({ name, k: words(name) }))
    .filter((c) => c.k.length > 0);
  const length = (k: string[]) => k.join('').length;

  const exact = only(
    keyed.filter((c) => c.k.join(' ') === w.join(' ')).map((c) => c.name),
  );
  if (exact) return exact;

  const prefixes = keyed.filter(
    (c) =>
      c.k.length < w.length &&
      length(c.k) >= MIN_PREFIX_LENGTH &&
      startsWithWords(w, c.k),
  );
  if (prefixes.length > 0) {
    const longest = Math.max(...prefixes.map((c) => c.k.length));
    return only(
      prefixes.filter((c) => c.k.length === longest).map((c) => c.name),
    );
  }

  if (length(w) < MIN_PREFIX_LENGTH) return null;
  return only(
    keyed
      .filter((c) => c.k.length > w.length && startsWithWords(c.k, w))
      .map((c) => c.name),
  );
};
